import { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LOGO_KEY = 'company_logo_url';
const NAME_KEY = 'company_name';

const toAbs = (raw) => {
  if (!raw) return null;
  // Already absolute (data URL or full http URL) → use as-is
  if (raw.startsWith('data:') || raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  // Relative /api/files path → prefix backend
  if (raw.startsWith('/')) return `${BACKEND}${raw}`;
  return raw;
};

// Pick the most reliable logo source. base64 always wins over the file URL
// because the file may have been wiped by a redeploy.
const pickLogoSource = (b64, url) => b64 || url || null;

export function useCompanyBranding() {
  const [logoUrl, setLogoUrl] = useState(() => toAbs(localStorage.getItem(LOGO_KEY)));
  const [companyName, setCompanyName] = useState(() => localStorage.getItem(NAME_KEY) || '');

  useEffect(() => {
    let alive = true;
    axios.get(`${BACKEND}/api/public/branding`)
      .then(r => {
        if (!alive) return;
        const raw = pickLogoSource(r.data?.company_logo_base64, r.data?.company_logo_url);
        if (raw) { localStorage.setItem(LOGO_KEY, raw); setLogoUrl(toAbs(raw)); }
        else { localStorage.removeItem(LOGO_KEY); setLogoUrl(null); }
        const name = r.data?.company_name || '';
        if (name) { localStorage.setItem(NAME_KEY, name); setCompanyName(name); }
        else { localStorage.removeItem(NAME_KEY); setCompanyName(''); }
      })
      .catch(() => { /* keep cached */ });

    const onChanged = (e) => {
      if (e.detail?.url !== undefined || e.detail?.base64 !== undefined) {
        const raw = pickLogoSource(e.detail?.base64, e.detail?.url);
        if (raw) { localStorage.setItem(LOGO_KEY, raw); setLogoUrl(toAbs(raw)); }
        else { localStorage.removeItem(LOGO_KEY); setLogoUrl(null); }
      }
      if (e.detail?.name !== undefined) {
        const name = e.detail.name || '';
        if (name) { localStorage.setItem(NAME_KEY, name); setCompanyName(name); }
        else { localStorage.removeItem(NAME_KEY); setCompanyName(''); }
      }
    };
    window.addEventListener('company-branding-changed', onChanged);
    return () => { alive = false; window.removeEventListener('company-branding-changed', onChanged); };
  }, []);

  return { logoUrl, companyName };
}

// Back-compat: some files import useCompanyLogo returning just the URL
export function useCompanyLogo() {
  const { logoUrl } = useCompanyBranding();
  return logoUrl;
}
